import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();

    // Guard anti double init
    if (this.__batchigInit) return;
    this.__batchigInit = true;

    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.__batchigInit = false;
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      window.__batchigLastCartOpenAt = Date.now();
      this.showDialog();
    }
  };

  open() {
    window.__batchigLastCartOpenAt = Date.now();
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

/* === Batchig – Refresh header + cart drawer (NO BLINK VERSION) === */

/**
 * Recharge la section "header" (qui contient le cart drawer)
 * MAIS met à jour uniquement le contenu du <dialog>,
 * sans remplacer le composant => pas de fermeture / réouverture.
 */
function batchigRefreshHeaderCartDrawer() {
  const root = window.Shopify?.routes?.root || '/';
  const url = `${root}?sections=header`;
  return fetch(url, { cache: 'no-store' })
    .then((response) => {
      if (!response.ok) throw new Error('Section fetch failed');
      return response.json();
    })
    .then((sections) => {
      const html = sections['header'];
      if (!html) throw new Error('header section missing');

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newDrawer = doc.querySelector('cart-drawer-component');
      const currentDrawer = document.querySelector('cart-drawer-component');
      if (!newDrawer || !currentDrawer) return;

      const newDialog = newDrawer.querySelector('dialog');
      const currentDialog = currentDrawer.querySelector('dialog');
      if (!newDialog || !currentDialog) return;

      // ✅ UPDATE DU CONTENU UNIQUEMENT
      currentDialog.innerHTML = newDialog.innerHTML;
    })
    .catch((err) => {
      console.error('Batchig drawer refresh failed', err);
    });
}

/* === Batchig – Upsell arrows (CAPTURE) === */

document.addEventListener(
  'click',
  function (event) {
    const arrow = event.target.closest('[data-upsell-arrow]');
    if (!arrow) return;

    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();

    const direction = arrow.getAttribute('data-upsell-arrow');
    const wrapper = arrow.closest('.batchig-upsell__carousel');
    if (!wrapper) return;

    const track = wrapper.querySelector('[data-upsell-track]');
    if (!track) return;

    const amount = Math.max(120, Math.round(track.clientWidth * 0.8));
    const nextLeft = direction === 'right' ? track.scrollLeft + amount : track.scrollLeft - amount;

    track.scrollTo({ left: nextLeft, behavior: 'smooth' });
  },
  true
);

// Empêche la fermeture du drawer quand on interagit avec l’upsell
// ✅ IMPORTANT: scope au drawer uniquement (sinon peut bloquer l'icône panier header)
['pointerdown', 'mousedown', 'touchstart', 'wheel'].forEach((evtName) => {
  document.addEventListener(
    evtName,
    function (event) {
      const drawer = event.target.closest('cart-drawer-component');
      if (!drawer) return; // ✅ ne bloque jamais hors drawer

      if (!event.target.closest('.batchig-upsell')) return;

      event.stopPropagation();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
    },
    true
  );
});

/* === Batchig – Add to cart AJAX (upsell forms) === */

document.addEventListener('submit', function (event) {
  const form = event.target.closest('[data-batchig-upsell-form]');
  if (!form) return;

  event.preventDefault();

  const submitBtn = form.querySelector('button[type="submit"]');
  if (submitBtn) submitBtn.disabled = true;

  const formData = new FormData(form);
  const qty = parseInt(formData.get('quantity') || '1', 10);

  const payload = {
    id: formData.get('id'),
    quantity: qty,
  };

  fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  })
    .then((response) => {
      if (!response.ok) throw new Error('Add to cart failed');
      return response.json();
    })
    .then(async () => {
      // ✅ 1) récupérer le panier à jour (total réel)
      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await cartRes.json();

      // ✅ 2) Update badge header (fiable) : sessionStorage + appel direct au composant
      try {
        sessionStorage.setItem(
          'cart-count',
          JSON.stringify({
            value: String(cart.item_count),
            timestamp: Date.now(),
          })
        );
      } catch (_) {}

      const cartIcon = document.querySelector('cart-icon');
      if (cartIcon && typeof cartIcon.renderCartBubble === 'function') {
        // comingFromProductForm = false => SET total (pas un +)
        cartIcon.renderCartBubble(cart.item_count, false, true);
      }

      // (optionnel) event thème
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            data: {
              itemCount: cart.item_count,
              source: 'drawer-upsell',
            },
          },
        })
      );

      // ✅ 3) garder/ouvrir le drawer
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    })
    .catch((error) => {
      console.error('Batchig upsell add-to-cart error', error);
    })
    .finally(() => {
      if (submitBtn) submitBtn.disabled = false;
    });
});

/* Empêcher propagation du clic sur la croix */
// ✅ IMPORTANT: scope au drawer uniquement (sinon peut bloquer l'icône panier header)
document.addEventListener(
  'click',
  function (event) {
    const drawer = event.target.closest('cart-drawer-component');
    if (!drawer) return;

    const closeBtn = event.target.closest('.cart-drawer__close-button');
    if (!closeBtn) return;

    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();
  },
  true
);

/* Empêcher fermeture sur summary / discount */
// ✅ IMPORTANT: scope au drawer uniquement (sinon peut bloquer l'icône panier header)
document.addEventListener(
  'click',
  function (event) {
    const drawer = event.target.closest('cart-drawer-component');
    if (!drawer) return;

    const summaryClickable = event.target.closest('.cart-drawer__summary, .cart-drawer__summary *');
    if (!summaryClickable) return;

    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();
  },
  true
);

/* === Batchig – Refresh after REAL cart mutations (STABILISÉ) === */
(function batchigInstallCartMutationHooks() {
  if (window.__batchigCartHooksInstalled) return;
  window.__batchigCartHooksInstalled = true;

  let refreshTimer = null;
  let refreshInFlight = false;
  let lastRefreshAt = 0;

  function isCartMutationUrl(url) {
    if (!url) return false;
    return (
      url.includes('/cart/change') ||
      url.includes('/cart/update') ||
      url.includes('/cart/add') ||
      url.includes('/cart/clear')
    );
  }

  function scheduleRefresh() {
    const now = Date.now();

    // ⛔ Ignore refresh juste après une ouverture volontaire
    if (window.__batchigLastCartOpenAt && now - window.__batchigLastCartOpenAt < 800) return;

    if (refreshInFlight) return;
    if (now - lastRefreshAt < 500) return;

    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(async () => {
      if (!document.querySelector('cart-drawer-component')) return;

      refreshInFlight = true;
      lastRefreshAt = Date.now();

      try {
        await batchigRefreshHeaderCartDrawer();
      } finally {
        setTimeout(() => {
          refreshInFlight = false;
        }, 300);
      }
    }, 120);
  }

  // --- Hook FETCH ---
  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const res = await originalFetch.apply(this, args);

    try {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;
      if (isCartMutationUrl(url) && res && res.ok) scheduleRefresh();
    } catch (e) {}

    return res;
  };

  // --- Hook XHR ---
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this.__batchig_url = url;
    return originalOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function (...args) {
    this.addEventListener('load', () => {
      try {
        if (isCartMutationUrl(this.__batchig_url) && this.status >= 200 && this.status < 300) {
          scheduleRefresh();
        }
      } catch (e) {}
    });

    return originalSend.apply(this, args);
  };
})();

/* === Batchig — Safari cart thumbnail fix (force inline styles) === */
(function batchigSafariCartThumbFix() {
  const isSafari =
    /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
    (navigator.vendor && navigator.vendor.includes('Apple'));

  if (!isSafari) return;
  if (window.__batchigSafariThumbFixInstalled) return;
  window.__batchigSafariThumbFixInstalled = true;

  function applyFix(root = document) {
    const imgs = root.querySelectorAll('cart-drawer-component img.cart-items__media-image');
    if (!imgs.length) return;

    imgs.forEach((img) => {
      const container = img.closest('a.cart-items__media-container');
      if (container) {
        container.style.overflow = 'hidden';
        container.style.position = 'relative';
        container.style.display = 'block';
      }

      img.style.objectFit = 'contain';
      img.style.objectPosition = 'center';
      img.style.transform = 'none';
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.maxWidth = '100%';
      img.style.maxHeight = '100%';

      img.style.position = 'absolute';
      img.style.top = '0';
      img.style.right = '0';
      img.style.bottom = '0';
      img.style.left = '0';
      img.style.display = 'block';
    });
  }

  applyFix(document);

  const drawer = document.querySelector('cart-drawer-component');
  const dialog = drawer?.querySelector('dialog');
  if (!dialog) return;

  let rafId = null;
  const obs = new MutationObserver(() => {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => applyFix(dialog));
  });

  obs.observe(dialog, { childList: true, subtree: true });
})();

/* === Batchig — Discovery Kit button handler === */
(function () {
  if (window.__batchigDiscoveryKitInstalled) return;
  window.__batchigDiscoveryKitInstalled = true;

  async function addVariant(variantId) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
    });

    if (!res.ok) throw new Error('Add to cart failed');
    return res.json();
  }

  function dispatchCartUpdate() {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          data: {
            itemCount: 1,
            source: 'product-form-component',
          },
        },
      })
    );
  }

  function openDrawer() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }
  }

  document.addEventListener(
    'click',
    async (event) => {
      const btn = event.target.closest('.js-discovery-kit');
      if (!btn) return;

      event.preventDefault();
      event.stopPropagation();

      const variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;

      btn.classList.add('is-loading');
      btn.style.pointerEvents = 'none';

      try {
        await addVariant(variantId);

        // ✅ badge panier
        dispatchCartUpdate();

        // ✅ ouverture du drawer
        openDrawer();
      } catch (e) {
        console.error('[Batchig Discovery Kit]', e);
      } finally {
        btn.classList.remove('is-loading');
        btn.style.pointerEvents = '';
      }
    },
    true
  );
})();
